import {
  GET_DISTRIBUTOR_GROUPS_SUCCESS,
  SELECT_FSE,
  selectFSE,
  sessionTerminated,
  setAmloStatus,
  setDistributorGroup,
  setUserAdvisor,
  silentRenewError,
  updateFSE,
  updateUserImage,
  USER_FOUND,
  userExpired,
  userExpiring,
  userFound,
  userSignedOut,
} from 'identity/actions'

import * as identityService from 'identity/service'
import { getDistributorGroupForUser } from 'core/service/distributor'
import { getAdvisor } from 'e-submission/domain/service'
import {
  getDistributorGroups,
  getIdentityProfileImage,
  getIdentityUser,
  getIdentityUserAdvisor,
} from 'identity/selectors'
import {
  evaluateAmloAccess,
  isAmloApplicable,
  isAmloEnabledByMiddlewareConfig,
  isCheckAmloEnabled,
  REASON,
} from 'identity/amlo'
import middlewareSvc from 'core/service/advisorzone-middleware/request-wrapper'
import { getToggles } from 'quick-quote/feature-toggles'
import messages from 'quick-quote/constants/messages'
import { setNotificationMessage } from 'quick-quote/notification/actions'
// import { logoutFromIDP } from 'identity/service'

import _ from 'lodash'
import { apply, call, put, select, spawn, take, takeEvery, takeLatest } from 'redux-saga/effects'
import { isNil, isNotNil } from 'core/service/lib/type-check'
import { UPDATE_USER, UPDATE_ADDITIONAL_USER_DETAILS, UPDATE_BANK_BRANCH } from './actions'

export function* onUpdateUser(userInfoResponse) {
  const { payload } = userInfoResponse
  const error = _.get(payload, 'profile.error', undefined)
  if (error === messages.LOGIN_LIMIT_SESSION_EXCEEDED) {
    yield put(setNotificationMessage({ type: 'LoginLimitSessionExceeded' }))
    // yield put(logoutFromIDP())
  }
}

// TODO: test if image fetch works after flow change
export function* onGetUserImage(identityService, action) {
  const userImage = yield select(getIdentityProfileImage)
  if (isNotNil(userImage)) {
    return
  }

  if (isNil(action)) {
    return
  }

  const user = action.payload
  if (user.profile.provider === 'facebook') {
    const { access_token: fbAccessToken } = yield call(identityService.getFBToken, user.access_token, user.token_type)
    const fbResult = yield call(identityService.getFBUserImage, fbAccessToken)
    yield put(updateUserImage(fbResult.picture.data.url))
  }
}

export function* subscribeUserManagerEvent(userManager, store) {
  if (getToggles().ENABLE_AUTHORISATION_CODE_FLOW) {
    return
  }
  userManager.events.addUserLoaded((user) => {
    store.dispatch(userFound(user))
  })
  userManager.events.addSilentRenewError((error) => {
    store.dispatch(silentRenewError(error.message))
  })
  userManager.events.addAccessTokenExpired(() => {
    store.dispatch(userExpired())
  })
  userManager.events.addAccessTokenExpiring(() => {
    store.dispatch(sessionTerminated())
  })
  userManager.events.addUserUnloaded(() => {
    store.dispatch(userExpiring())
  })
  userManager.events.addUserSignedOut(() => {
    store.dispatch(userSignedOut())
  })

  const user = yield apply(userManager, userManager.getUser)
  if (!user || user.expired) {
    yield put(userExpired())
  } else {
    yield put(userFound(user))
  }
}

export function* updateUserDistributorGroup() {
  const user = yield select(getIdentityUser)
  const distributorGroups = yield select(getDistributorGroups)
  if (_.isEmpty(distributorGroups)) {
    const { payload } = yield take(GET_DISTRIBUTOR_GROUPS_SUCCESS)
    const userDistributorGroup = yield call(getDistributorGroupForUser, payload, user)
    yield put(setDistributorGroup(userDistributorGroup))
  } else {
    const userDistributorGroup = yield call(getDistributorGroupForUser, distributorGroups, user)
    yield put(setDistributorGroup(userDistributorGroup))
  }
}

export function* onUpdateBankBranch(action) {
  if (!getToggles().ENABLE_PRE_POPULATE_FSE) {
    return
  }

  const selectedOffice = action.payload
  if (_.get(selectedOffice, 'fseCode')) {
    const user = yield select(getIdentityUser)
    const fse = yield call(getAdvisor, user, selectedOffice.fseCode, 'fse')
    yield put(selectFSE(fse))
  } else {
    yield put(updateFSE(undefined))
  }
}

export function* onSelectFSE(action) {
  const selectedFSE = action.payload
  if (
    !_.isUndefined(selectedFSE) &&
    !_.isUndefined(selectedFSE.party) &&
    !_.isUndefined(selectedFSE.party.firstName) &&
    !_.isUndefined(selectedFSE.party.lastName)
  ) {
    const fse = {
      firstName: selectedFSE.party.firstName,
      lastName: selectedFSE.party.lastName,
      advisorCode: selectedFSE.advisorCode,
      advisorId: selectedFSE.advisorId,
      corporateEmail: _.get(
        _.find(selectedFSE.contactMethods.emailContact, (e) => e.type === 'corporate'),
        'url'
      ),
      personalEmail: _.get(
        _.find(selectedFSE.contactMethods.emailContact, (e) => e.type !== 'corporate'),
        'url'
      ),
    }
    yield put(updateFSE(fse))
  } else {
    yield put(updateFSE(undefined))
  }
}

export function* getSelfAdvisor() {
  const user = yield select(getIdentityUser)
  const agentCode = _.get(user, 'profile.agent_code')
  const agentType = _.get(user, 'profile.agent_type')
  if (!agentCode || !agentType) {
    return null
  }

  const advisor = yield call(getAdvisor, user, agentCode, agentType)
  yield put(setUserAdvisor(advisor))

  return advisor
}

export function* getCachedSelfAdvisor() {
  const cached = yield select(getIdentityUserAdvisor)
  if (cached) {
    return cached
  }

  return yield call(getSelfAdvisor)
}

export function* isAmloEnabledByMiddleware() {
  try {
    const response = yield middlewareSvc.getRequest('config', { ignoreAccessToken: true })
    return isAmloEnabledByMiddlewareConfig(_.get(response, 'data'))
  } catch (error) {
    return true
  }
}

export function* checkAmloAccess() {
  if (!isCheckAmloEnabled()) {
    yield put(setAmloStatus({ hasAccess: true, reason: REASON.TOGGLE_OFF }))

    return
  }

  const middlewareEnabled = yield* isAmloEnabledByMiddleware()
  if (!middlewareEnabled) {
    yield put(setAmloStatus({ hasAccess: true, reason: REASON.MIDDLEWARE_OFF }))

    return
  }

  const user = yield select(getIdentityUser)
  const agentType = _.get(user, 'profile.agent_type')
  if (!isAmloApplicable(agentType)) {
    yield put(setAmloStatus({ hasAccess: true, reason: REASON.OUT_OF_SCOPE }))

    return
  }

  try {
    const advisor = yield call(getCachedSelfAdvisor)
    const result = evaluateAmloAccess({ advisor })
    yield put(setAmloStatus(result))
  } catch (error) {
    const status = _.get(error, 'response.status')
    const isDown = !_.get(error, 'response')
    if (isDown || (typeof status === 'number' && status >= 500)) {
      yield put(setAmloStatus({ hasAccess: false, reason: REASON.ADVISOR_API_ERROR }))
      yield put(setNotificationMessage({ type: 'AmloAdvisorError' }))
      return
    }

    throw error
  }
}

export default function*(userManager, store) {
  yield [
    spawn(subscribeUserManagerEvent, userManager, store),
    takeLatest(UPDATE_USER, onUpdateUser),
    takeEvery(USER_FOUND, onGetUserImage, identityService),
    takeEvery([USER_FOUND], updateUserDistributorGroup),
    takeEvery([UPDATE_BANK_BRANCH], onUpdateBankBranch),
    takeEvery(UPDATE_ADDITIONAL_USER_DETAILS, updateUserDistributorGroup),
    takeLatest(SELECT_FSE, onSelectFSE),
    takeLatest(USER_FOUND, checkAmloAccess),
    takeLatest(UPDATE_ADDITIONAL_USER_DETAILS, checkAmloAccess),
  ]
}
